import * as Sentry from "@sentry/nextjs";

const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;

let initialized = false;

function initSentry() {
  if (initialized || !SENTRY_DSN) return;
  initialized = true;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    // Tracing off: spans burned 80% of the monthly reserved quota by
    // 2026-06-05 (4M/5M) while the perf product goes unused - errors
    // are the only Sentry signal we act on. Re-enable selectively
    // (low rate) only for a deliberate perf investigation.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Don't send PII
    sendDefaultPii: false,
    // Not faults of ours (owner, 2026-11-03: these buried the real ones):
    // the browser losing its network, a listing the site removed, the
    // API's deliberate 503, Chrome's in-place translation breaking
    // React's DOM, and storage a private window refuses.
    ignoreErrors: [
      /^Network request failed$/,
      /network_fetch_failed/,
      /page temporarily unavailable/,
      /^listing removed$/,
      /^seo api unavailable/,
      /Elan tapılmadı|Объявление не найдено|Listing not found/,
      /insertBefore|removeChild/,
      /localStorage|sessionStorage/,
    ],
  });
}

/** Errors the product answers by itself: never a Sentry event. */
function isExpected(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === "NetworkError" || error.name === "ListingRemovedError" || error.name === "SeoUnavailableError") return true;
  if (error.name === "UserInputError") {
    const status = (error as { status?: number }).status;
    return typeof status !== "number" || status < 500;
  }
  return false;
}

export function captureWarning(message: string, extra?: Record<string, unknown>) {
  if (!SENTRY_DSN) return;
  initSentry();
  Sentry.captureMessage(message, {
    level: "warning",
    extra,
  });
}

export function captureError(error: unknown, extra?: Record<string, unknown>) {
  if (!SENTRY_DSN || isExpected(error)) return;
  initSentry();
  if (error instanceof Error) {
    Sentry.captureException(error, { extra });
  } else {
    Sentry.captureMessage(String(error), { level: "error", extra });
  }
}

/** Waits (at most `timeoutMs`) until what was captured has been sent: the
 *  error page reports before it reloads, and a reload would cut it off. */
export async function flushErrors(timeoutMs: number): Promise<void> {
  if (!SENTRY_DSN || !initialized) return;
  await Sentry.flush(timeoutMs).catch(() => false);
}
